import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Star, ShoppingCart } from 'lucide-react';
import { addToCart } from '../api/api';

const ProductCard = ({ product }) => {
  const navigate = useNavigate();
  const [selectedWeight, setSelectedWeight] = useState('250G');
  const [selectedColor, setSelectedColor] = useState('Siyah');
  const [selectedGrind, setSelectedGrind] = useState('Çekirdek (Öğütme İstemiyorum)');
  const [addingToCart, setAddingToCart] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const isEspressoMachine = product.category === 'Espresso Makineleri';

  // Espresso makinesi için seçilen renge göre görsel
  const getProductImage = () => {
    if (isEspressoMachine && product.name.includes('Fiamma Compass')) {
      if (selectedColor === 'Beyaz') {
        return '/images/fiamma-compass-white.jpg';
      } else {
        return '/images/fiamma-compass-black.jpg';
      }
    }
    return product.image;
  };

  // Ürün türüne göre öğütme seçeneklerini belirle
  const getGrindOptions = () => {
    // Espresso makineleri için öğütme yok
    if (isEspressoMachine) {
      return [];
    }
    
    // Sıcak Toz Ürünleri için öğütme yok
    if (product.category === 'JUNTO® Sıcak Toz Ürünler') {
      return [];
    }
    
    // Türk Kahvesi için sadece öğütülmüş
    if (product.name.includes('TÜRK KAHVES')) {
      return ['Öğütülmüş (Hazır)'];
    }
    
    // Diğer kahveler için tüm seçenekler
    return [
      'Çekirdek (Öğütme İstemiyorum)',
      'Chemex',
      'V60',
      'Espresso',
      'Moka Pot',
      'Filtre Kahve Makinesi',
      'Aeropress',
      'French Press',
      'Cold Brew'
    ];
  };

  const grindOptions = getGrindOptions();
  
  // İlk yüklemede doğru default değeri set et
  React.useEffect(() => {
    if (product.name.includes('TÜRK KAHVES')) {
      setSelectedGrind('Öğütülmüş (Hazır)');
    }
    if (isEspressoMachine && product.variants?.[0]?.color) {
      setSelectedColor(product.variants[0].color);
    }
  }, [product.name, isEspressoMachine, product.variants]);

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${
          i < Math.floor(rating)
            ? 'fill-amber-400 text-amber-400'
            : 'text-gray-300'
        }`}
      />
    ));
  };

  const getSelectedPrice = () => {
    if (isEspressoMachine) {
      const variant = product.variants?.find(v => v.color === selectedColor);
      return variant?.price || product.base_price;
    } else {
      const variant = product.variants?.find(v => v.weight === selectedWeight);
      return variant?.price || product.base_price;
    }
  };

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setAddingToCart(true);
    try {
      const sessionId = localStorage.getItem('session_id');
      
      if (isEspressoMachine) {
        await addToCart(sessionId, product.id, 1, null, null, selectedColor);
      } else {
        await addToCart(sessionId, product.id, 1, selectedWeight, selectedGrind);
      }
      
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
      }, 2000);
      
      // Reload page to update cart count
      window.location.reload();
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('Sepete eklenirken bir hata oluştu');
    } finally {
      setAddingToCart(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 group h-full flex flex-col" data-testid={`product-card-${product.id}`}>
      <Link to={`/product/${product.id}`} className="flex-1 flex flex-col">
        <div className="relative overflow-hidden aspect-square">
          <img
            src={getProductImage()}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            loading="lazy"
          />
          {product.is_featured && (
            <div className="absolute top-4 right-4 bg-junto-red text-white px-3 py-1 rounded-full text-xs font-bold">
              ⭐ Öne Çıkan
            </div>
          )}
          {product.stock < 20 && product.stock > 0 && (
            <div className="absolute top-4 left-4 bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold">
              Son {product.stock} Ürün!
            </div>
          )}
          {product.stock === 0 && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
              <span className="text-white text-xl font-bold">Tükendi</span>
            </div>
          )}
        </div>

        <div className="p-6 flex-1 flex flex-col">
          <div className="mb-2">
            <span className="text-xs font-medium text-amber-600 bg-amber-50 px-2 py-1 rounded">
              {product.category}
            </span>
          </div>

          <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-junto-red transition-colors min-h-[3.5rem]" data-testid={`product-name-${product.id}`}>
            {product.name}
          </h3>

          <p className="text-sm text-gray-600 mb-3 line-clamp-2 min-h-[2.5rem]">
            {product.description}
          </p>

          <div className="flex items-center space-x-1 mb-4">
            {renderStars(product.rating)}
            <span className="text-sm text-gray-600 ml-2">
              ({product.reviews_count})
            </span>
          </div>

          <div className="flex items-center justify-between mt-auto">
            <div>
              <span className="text-2xl font-bold text-junto-red" data-testid={`product-price-${product.id}`}>
                ₺{getSelectedPrice()?.toFixed(2)}
              </span>
              {!isEspressoMachine && (
                <span className="text-sm text-gray-500 ml-2">{selectedWeight}</span>
              )}
            </div>
          </div>
        </div>
      </Link>

      {/* Seçenekler: Renk (Espresso) veya Gramaj & Öğütme (Kahve) */}
      <div className="px-6 pb-4 space-y-3" onClick={(e) => e.stopPropagation()}>
        {isEspressoMachine ? (
          /* Renk Seçimi - Espresso Makineleri için */
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-2">Renk:</label>
            <div className="flex gap-2">
              {product.variants?.map((variant) => (
                <button
                  key={variant.color}
                  onClick={(e) => {
                    e.preventDefault();
                    setSelectedColor(variant.color);
                  }}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition-all ${
                    selectedColor === variant.color
                      ? 'bg-junto-red text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {variant.color}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {/* Gramaj Seçimi - Kahve ürünleri için */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-2">Gramaj:</label>
              <div className="flex gap-2">
                {product.variants?.map((variant) => (
                  <button
                    key={variant.weight}
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedWeight(variant.weight);
                    }}
                    className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition-all ${
                      selectedWeight === variant.weight
                        ? 'bg-junto-red text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {variant.weight}
                  </button>
                ))}
              </div>
            </div>

            {/* Öğütme Seçimi - Sadece kahve ürünleri için */}
            {grindOptions.length > 0 && (
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">Öğütme:</label>
                <select
                  value={selectedGrind}
                  onChange={(e) => setSelectedGrind(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  className="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-junto-red focus:border-transparent"
                >
                  {grindOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </>
        )}
      </div>

      {/* Sepete Ekle Butonu */}
      <div className="px-6 pb-6">
        {showSuccess ? (
          <div className="w-full bg-green-500 text-white font-bold py-3 px-6 rounded-xl text-center">
            ✓ Sepete Eklendi!
          </div>
        ) : (
          <button
            onClick={handleAddToCart}
            disabled={addingToCart}
            className="w-full btn-primary bg-gradient-to-r from-junto-red to-junto-red-dark hover:from-junto-red-dark hover:to-junto-red-light text-white font-bold py-3 px-6 rounded-xl flex items-center justify-center space-x-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid={`add-to-cart-btn-${product.id}`}
          >
            <ShoppingCart className="w-5 h-5" />
            <span>{addingToCart ? 'Ekleniyor...' : 'Sepete Ekle'}</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default ProductCard;